import React from 'react';

import { Box, Icon, Text as UCLText } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import ActionButton from 'components/action-button';
import Modal, { ModalContent, ModalHeading } from 'components/modal';
import { useOrderDetails } from 'hooks/use-order-details';
import { useOrderContext } from 'state/order';
import { useReorder } from 'state/order/hooks/use-reorder';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';

const Container = Box.withConfig({
  alignItems: 'center',

  _text: {
    textAlign: 'center',
  },

  paddingTop: '$8',
  paddingRight: 0,
  paddingBottom: '$12',
  paddingLeft: 0,

  maxWidth: {
    base: 300,
    md: 500,
  },
});

const Heading = ModalHeading.withConfig({
  marginTop: '$12',
  marginRight: 0,
  marginBottom: '$4',
  marginLeft: 0,
});

const Text = UCLText.withConfig({
  marginTop: 0,
  marginRight: 0,
  marginBottom: '$12',
  marginLeft: 0,
});

const ErrorIcon = Icon.withConfig({
  variant: 'error',
  width: '$32',
  height: '$32',
});

interface IOrderCancelledModalProps {
  onDismiss: () => void;
  modalAppearanceEventMessage?: string;
}

const OrderCancelledModal: React.FC<React.PropsWithChildren<IOrderCancelledModalProps>> = ({
  onDismiss,
  modalAppearanceEventMessage = 'Order Cancelled',
}) => {
  const {
    serverOrder,
    reorder: { reordering },
  } = useOrderContext();
  const { order } = useOrderDetails(serverOrder?.rbiOrderId || null);
  const { handleReorderClick } = useReorder(order);
  const { formatMessage } = useIntl();

  const modalHeading = formatMessage({ id: 'cancelledOrderTitle' });
  const modalBody = formatMessage({ id: 'cancelledOrderAction' });

  return (
    <Modal
      onDismiss={onDismiss}
      amplitudeEventData={{
        modalAppearanceEventMessage,
        modalMessage: modalBody,
      }}
    >
      <ModalContent alignItems="center">
        <Container>
          <ErrorIcon {...hiddenAccessibilityPlatformProps} />
          <Heading>{modalHeading}</Heading>
          <Text>{modalBody}</Text>
          <ActionButton onPress={handleReorderClick} isLoading={reordering}>
            {formatMessage({ id: 'reorder' })}
          </ActionButton>
        </Container>
      </ModalContent>
    </Modal>
  );
};

export default OrderCancelledModal;
